import { useNavigate, useLocation } from 'react-router-dom';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import Logo from '@/components/Logo';
import { Upload, Menu, X } from 'lucide-react';

export default function HeaderNav() {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navLinks = [
    { label: 'Home', path: '/' },
    { label: 'Pricing', path: '/impact-window-cost-miami' },
    { label: 'Permits', path: '/impact-window-permits-miami-dade' },
    { label: 'Case Studies', path: '/case-studies' }
  ];

  const isActive = (path) => location.pathname === path;

  const handleCTAClick = () => {
    // Track CTA click
    if (window.gtag) {
      window.gtag('event', 'cta_click', {
        location: 'header',
        event_category: 'engagement',
        event_label: 'Get My Exact Price'
      });
      window.gtag('event', 'cta_clicked_header', {
        event_category: 'cta_tracking',
        event_label: 'Header CTA Button'
      });
    }
    if (window.fbq) {
      window.fbq('track', 'ViewContent', { content_name: 'Header CTA Click' });
    }
    setMobileMenuOpen(false);
    navigate('/get-quote');
  };

  const handleNavClick = (path) => {
    setMobileMenuOpen(false);
    navigate(path);
  };

  return (
    <>
      <nav className="sticky top-0 z-50 bg-white/95 backdrop-blur border-b border-[hsl(var(--border))]" data-testid="global-header-nav">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between h-16">
          <div className="flex items-center gap-4 sm:gap-8">
            <Logo variant="light" size="xsmall" onClick={() => handleNavClick('/')} className="cursor-pointer" />
            
            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-6">
              {navLinks.map((link) => (
                <button
                  key={link.path}
                  onClick={() => handleNavClick(link.path)}
                  className={`text-sm font-medium transition-colors hover:text-[var(--brand-ocean-700)] ${
                    isActive(link.path)
                      ? 'text-[var(--brand-ocean-700)] font-semibold'
                      : 'text-[var(--brand-slate-600)]'
                  }`}
                  data-testid={`nav-link-${link.label.toLowerCase().replace(' ', '-')}`}
                >
                  {link.label}
                </button>
              ))}
            </div>
          </div>

          {/* Desktop CTA */}
          <div className="hidden md:block">
            <Button
              onClick={handleCTAClick}
              data-testid="header-cta-get-my-exact-price"
              className="bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] rounded-xl h-11 px-6 text-sm font-semibold shadow-sm hover:shadow-md transition-all"
            >
              <Upload className="mr-2 h-4 w-4" />
              Get My Price
            </Button>
          </div>

          {/* Mobile: CTA + Menu Toggle */}
          <div className="flex items-center gap-2 md:hidden">
            <Button
              onClick={handleCTAClick}
              size="sm"
              data-testid="header-cta-mobile"
              className="bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] rounded-lg h-9 px-4 text-xs font-semibold"
            >
              <Upload className="h-3 w-3" />
            </Button>
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2 text-[var(--brand-slate-600)] hover:text-[var(--brand-ocean-700)] transition-colors"
              aria-label="Toggle mobile menu"
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu Dropdown */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-[hsl(var(--border))] bg-white">
            <div className="px-4 py-3 space-y-1">
              {navLinks.map((link) => (
                <button
                  key={link.path}
                  onClick={() => handleNavClick(link.path)}
                  className={`block w-full text-left px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                    isActive(link.path)
                      ? 'bg-[var(--brand-ocean-100)] text-[var(--brand-ocean-700)] font-semibold'
                      : 'text-[var(--brand-slate-600)] hover:bg-[hsl(var(--secondary))]'
                  }`}
                  data-testid={`mobile-nav-link-${link.label.toLowerCase().replace(' ', '-')}`}
                >
                  {link.label}
                </button>
              ))}
              <div className="pt-2">
                <Button
                  onClick={handleCTAClick}
                  className="w-full bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] rounded-xl h-12 text-sm font-semibold"
                >
                  <Upload className="mr-2 h-4 w-4" />
                  Get My Price
                </Button>
              </div>
            </div>
          </div>
        )}
      </nav>
    </>
  );
}
